import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CLIENT_TESTIMONIALS } from '../data/clientReviewsPageData';
import { SITE_TRUST_STATS } from '../data/siteTrustStats';
import { HOME_FEATURED_TESTIMONIAL_IDS, HOME_TESTIMONIAL_TAGS_MAX } from '../data/homeFeaturedTestimonials';
import './HomeTestimonialsCarousel.css';

const AUTO_MS = 4000;
const GAP_PX = 11;

function StarRow({ rating }) {
  const r = Math.min(5, Math.max(0, rating || 0));
  return (
    <div className="home-testimonials-stars" aria-label={`${r} out of 5 stars`} role="img">
      <span className="home-testimonials-stars-fill">{'★'.repeat(r)}</span>
      {r < 5 && (
        <span className="home-testimonials-stars-empty" aria-hidden="true">
          {'☆'.repeat(5 - r)}
        </span>
      )}
    </div>
  );
}

function HomeTestimonialsCarousel() {
  const viewportRef = useRef(null);
  const pausedRef = useRef(false);
  const touchStartX = useRef(null);

  const items = useMemo(
    () =>
      HOME_FEATURED_TESTIMONIAL_IDS.map((id) => CLIENT_TESTIMONIALS.find((t) => t.id === id)).filter(Boolean),
    []
  );

  const scrollStep = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return 0;
    const card = el.querySelector('.home-testimonials-card-wrap');
    if (!card) return 0;
    return card.offsetWidth + GAP_PX;
  }, []);

  const scrollNext = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const step = scrollStep();
    const max = el.scrollWidth - el.clientWidth;
    if (step <= 0) return;
    if (el.scrollLeft >= max - 4) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: step, behavior: 'smooth' });
    }
  }, [scrollStep]);

  const scrollPrev = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const step = scrollStep();
    if (step <= 0) return;
    if (el.scrollLeft <= 4) {
      el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: -step, behavior: 'smooth' });
    }
  }, [scrollStep]);

  useEffect(() => {
    if (!items.length) return undefined;
    const id = setInterval(() => {
      if (!pausedRef.current) scrollNext();
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [items.length, scrollNext]);

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };

  const onTouchStart = (e) => {
    if (e.touches?.[0]) touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || !e.changedTouches?.[0]) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) scrollNext();
    else scrollPrev();
  };

  if (!items.length) return null;

  return (
    <section
      className="home-testimonials-section animate-on-scroll"
      aria-labelledby="home-testimonials-heading"
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <div className="container home-testimonials-head">
        <h2 id="home-testimonials-heading" className="home-testimonials-h2">
          What Our Clients Say
        </h2>
        <p className="home-testimonials-sub">
          Trusted by {SITE_TRUST_STATS[0].value} clients across Ahmedabad for valet parking, manpower, and housekeeping
          services.
        </p>
        <p className="home-testimonials-seo">
          From corporate towers to societies, teams choose us for dependable{' '}
          <Link to="/valet-parking-ahmedabad">valet parking in Ahmedabad</Link>, responsive{' '}
          <Link to="/manpower-services-ahmedabad">manpower services in Ahmedabad</Link>, and detail-focused{' '}
          <Link to="/housekeeping-services-ahmedabad">housekeeping services in Ahmedabad</Link>—with clear pricing and
          professional crews.
        </p>
      </div>

      <div className="home-testimonials-carousel-shell">
        <button
          type="button"
          className="home-testimonials-arrow home-testimonials-arrow--prev"
          onClick={scrollPrev}
          aria-label="Previous testimonial"
        >
          ‹
        </button>
        <div
          className="home-testimonials-viewport"
          ref={viewportRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {items.map((t) => (
            <div key={t.id} className="home-testimonials-card-wrap">
              <article className="home-testimonials-card">
                <span className="home-testimonials-quote" aria-hidden="true">
                  “
                </span>
                <StarRow rating={t.rating} />
                <p className="home-testimonials-text">{t.text}</p>
                <p className="home-testimonials-name">{t.name}</p>
                {t.tags && t.tags.length > 0 && (
                  <ul className="home-testimonials-tags">
                    {t.tags.slice(0, HOME_TESTIMONIAL_TAGS_MAX).map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                )}
              </article>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="home-testimonials-arrow home-testimonials-arrow--next"
          onClick={scrollNext}
          aria-label="Next testimonial"
        >
          ›
        </button>
      </div>

      <div className="container home-testimonials-cta-wrap">
        <Link to="/client-reviews-ahmedabad" className="home-testimonials-cta">
          View All Reviews
        </Link>
      </div>
    </section>
  );
}

export default HomeTestimonialsCarousel;
